import React, { lazy, Suspense } from "react";
import { useState, useEffect, useRef } from "react";
import { CheckCircle, HandHeart, MagnifyingGlass, MapPin, Medal, SealCheck, Storefront } from "@phosphor-icons/react";
import { useGetProductsQuery } from '../../app/config/redux/slices/api/productApiSlice';

const ProductNew = lazy(() => import("../components/ProductNew"));

const vendors = [
  { id: 2, name: "Gramika", loc: "Kerala", cover: "https://app.villagezone.shop/images/20240418073642.jpeg", tag: "Kerala food maker", cats: ["Pickles"], tagline: "Handmade pantry favourites, made with care" },
  { id: 13, name: "Parister Pickles", loc: "Kerala", cover: "https://app.villagezone.shop/images/20240418135640.jpeg", tag: "Local food seller", cats: ["Pickles"], tagline: "Traditional flavours for everyday meals" },
  { id: 19, name: "Jojos Kaitharikada", loc: "Kerala", cover: "https://villagezone.shop/images/108-onam-full-tissue-weave-293.png", tag: "Traditional textiles", cats: ["Kerala Sarees"], tagline: "Timeless Kerala weaves for meaningful celebrations" },
];

const testimonials = [
  { quote: "I have tried chammanthi podi and dates pickles — they were yummy. My kids liked them too!", name: "Elza George", meta: "Angamaly", avatar: "👩", stars: 5 },
  { quote: "Gramika's products are very good. Dates and lemon pickle are very tasty with high quality.", name: "Mani", meta: "Kothamangalam", avatar: "👨", stars: 5 },
  { quote: "Fresh, flavourful and nutritious — a perfect healthy option that also saves valuable time in today's busy life.", name: "Sheffy Thomas", meta: "Kottayam", avatar: "🧕", stars: 5 },
  { quote: "Chammanthipodi and pickle were amazing. It had a homely taste and gave me a nostalgic feeling. Highly recommended!", name: "Nimmy Philip", meta: "Kakkanad", avatar: "👩", stars: 5 },
  { quote: "Tried garlic pickle — the taste was nice. Everyone in the family liked it!", name: "Abhilash Urumese", meta: "Manjapra", avatar: "👨", stars: 5 },
  { quote: "The taste reminded me of my amma's recipe. Such authentic and nostalgic taste — truly wonderful.", name: "Aiswarya Suresh", meta: "Aluva", avatar: "👩", stars: 5 },
];

const heroTrustItems = ["Selected with care", "Carefully curated", "Authentic local products", "Supporting small businesses"];

const trustItems = [
  { icon: SealCheck, text: "Clear Product Details" },
  { icon: Medal, text: "Traditional Craftsmanship" },
  { icon: HandHeart, text: "Local Sellers" },
  { icon: CheckCircle, text: "Quality You Can Trust" },
];

const pillars = [
  { icon: Storefront, name: "Local Finds", desc: "Everyday essentials and special pieces in one place" },
  { icon: CheckCircle, name: "Details You Can Read", desc: "Straightforward information to help you choose well" },
  { icon: Medal, name: "Made for Meaningful Moments", desc: "Products for your home, family, and traditions" },
  { icon: HandHeart, name: "Supporting Local Brands", desc: "Backing small producers and local entrepreneurs" },
];

const curationChecks = [
  { icon: MagnifyingGlass, name: "Details Check", desc: "We look closely at product details, materials, and ingredients so you can choose confidently." },
  { icon: CheckCircle, name: "Quality Check", desc: "Each product is evaluated for consistency, authenticity, and the care taken in production." },
  { icon: HandHeart, name: "Maker Check", desc: "We make seller and product details clearer, so you know what you are choosing." },
];


const HomePageVillageZone = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [copiedCoupon, setCopiedCoupon] = useState(false);
  const [favs, setFavs] = useState({});

  const { data, isLoading, isFetching, error } = useGetProductsQuery({
    limit: 10,
    page: 1,
    quantity: 0,
    filters: [],
    search: ''
  });

  const products = data?.products?.data || []; // Use API data if available

  const toggleFav = (id) => setFavs(f => ({ ...f, [id]: !f[id] }));

  return (
    <>
      <div className="home-page">
        {/* HERO */}
        <section className="hero" style={{ padding: " 0 0", position: "relative" }}>
          <div className="hero-noise" />
          <div className="hero-left">
            <div className="hero-badge">Thoughtfully Curated, Locally Rooted</div>
            <h1 className="hero-h1">Good choices for your home and your <em>traditions</em></h1>
            <p className="hero-sub">From authentic local foods to timeless Kerala sarees, discover products selected for quality, care, and real value.</p>
            <div className="hero-actions">
              <a href="/shop" className="btn-primary">Explore the Market →</a>
              <a href="#kerala-sarees" className="btn-ghost">Shop Sarees</a>
            </div>
            <div className="hero-trust">
              {heroTrustItems.map((item) => <div className="hero-trust-item" key={item}><CheckCircle weight="fill" size={15} />{item}</div>)}
            </div>
          </div>
          <div className="hero-right">
            <img src="assets/images/style/cover-1-900.webp" alt="A selection of Village Zone food products" />
          </div>
        </section>

        {/* TRUST BAR */}
        <div className="trust-bar">
          {trustItems.map((t, i) => {
            const Icon = t.icon;
            return (
            <React.Fragment key={t.text}>
              <div key={i} className="trust-item">
                <span className="trust-icon"><Icon weight="duotone" size={21} /></span>
                <span>{t.text}</span>
              </div>
              {i < 3 && <div className="trust-divider" />}
            </React.Fragment>
          )})}
        </div>

        {/* KERALA SAREE LAUNCH */}
        <section className="saree-feature" id="kerala-sarees">
          <div className="saree-image-wrap">
            <img
              src="https://villagezone.shop/images/108-onam-full-tissue-weave-293.png"
              alt="Traditional Kerala off-white tissue saree with broad kasavu border"
            />
            <span className="saree-image-label">New arrival</span>
          </div>
          <div className="saree-content">
            <div className="section-label">New Collection</div>
            <h2 className="section-title">Timeless <em>Kerala Sarees</em>, beautifully chosen</h2>
            <p className="saree-copy">Our latest Onam arrival brings together a full tissue weave, a sleek plain border, and a beautifully detailed pallu—crafted for a soft, elegant drape and all-day comfort.</p>
            <ul className="saree-details">
              <li>Off-white full tissue weave with fine golden zari lines</li>
              <li>6.25 metres with a plain blouse piece</li>
              <li>Broad kasavu border and detailed pallu</li>
            </ul>
            <div className="saree-footer">
              <div><span>Starting with</span><strong>₹1,820</strong></div>
              <a href="/product-details/108-traditional-kerala-off-white-tissue-saree-decorated-with-fine-horizontal-golden-zari-lines-across-the-body-complemented-by-a-broad-kasavu-border" className="btn-primary">View the Saree →</a>
            </div>
          </div>
        </section>

        {/* ABOUT / WHY VILLAGE ZONE */}
        <section className="about">
          <div className="about-img-wrap">
            <div style={{ width: "100%", aspectRatio: "4/5", borderRadius: "24px", background: "linear-gradient(135deg,#b8d4ba 0%,#6b8f71 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "8rem", boxShadow: "0 24px 64px rgba(0,0,0,0.12)" }}>
              <img src="/assets/images/style/cover-2-800.webp" srcSet="/assets/images/style/cover-2-400.webp 400w,/assets/images/style/cover-2-800.webp 800w,/assets/images/style/cover-2-1100.webp 1100w" alt="Our Story" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "24px" }} />
            </div>
            <div className="about-leaf-badge">
              <Medal weight="duotone" size={28} />
              <strong>Est.</strong>
              <small>2022</small>
            </div>
          </div>
          <div className="about-right">
            <div className="section-label">Why Village Zone</div>
            <h2 className="section-title">Why Choose <em>Village Zone</em></h2>
            <p style={{ fontSize: "1rem", color: "var(--bark)", opacity: 0.7, lineHeight: 1.8 }}>One place for trusted local products—carefully curated for quality, authenticity, and the stories behind them.</p>
            <div className="about-pillars">
              {pillars.map((p, i) => {
                const Icon = p.icon;
                return (
                <div key={i} className="pillar">
                  <div className="pillar-icon"><Icon weight="duotone" size={28} /></div>
                  <div className="pillar-name">{p.name}</div>
                  <div className="pillar-desc">{p.desc}</div>
                </div>
              )})}
            </div>
          </div>
        </section>

        {/* PRODUCTS */}
        <section className="products">
          <div className="section-head">
            <div>
              <div className="section-label">Curated Picks</div>
              <h2 className="section-title">Featured <em>Products</em></h2>
            </div>
            <a href="/shop" className="see-all">All products →</a>
          </div>
          {/* <div className="product-tabs">
          {[
            { key: "all", label: "🛒 All" },
            { key: "food", label: "🍯 Food & Pantry" },
            { key: "health", label: "🌿 Health & Wellness" },
            { key: "beauty", label: "🧴 Beauty & Personal Care" },
            { key: "ingredients", label: "🌾 Natural Ingredients" },
          ].map(t => (
            <button key={t.key} className={`tab${activeTab === t.key ? " active" : ""}`} onClick={() => setActiveTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div> */}
          <Suspense fallback={<div></div>}>
          <div className="product-grid">
            {products.map(p => (
              <ProductNew product={p} />
            ))}
          </div>
          </Suspense>
        </section>

        {/* VENDORS */}
        <section className="vendors">
          <div className="section-head">
            <div>
              <div className="section-label">Our Community</div>
              <h2 className="section-title">Meet the Brands Behind <em>Your Products</em></h2>
              <p className="section-sub">From family-run food makers to traditional textile sellers, meet the people behind your products.</p>
            </div>
            <a href="/vendor" className="see-all">All brands →</a>
          </div>
          <div className="vendor-grid">
            {vendors.map((v, i) => (
              <div key={i} className="home-vendor-card">
                <div className="vendor-cover">
                  <img className="vendor-cover-image" src={v.cover} alt={`${v.name} products`} />
                  <div className="vendor-tag">{v.tag}</div>
                </div>
                <div className="vendor-body">
                  <div className="vendor-name">{v.name}</div>
                  <div className="vendor-loc"><MapPin weight="fill" size={14} /> {v.loc}</div>
                  <div className="vendor-tagline">{v.tagline}</div>
                  <div className="vendor-footer">
                    <div className="vendor-cats">
                      {v.cats.map((c, ci) => <span key={ci} className="vendor-cat-pill">{c}</span>)}
                    </div>
                    <a href={`/shop?vendor=${v.id}`} className="vendor-shop-btn">View Products →</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CURATION SECTION */}
        <section className="curation">
          <div style={{ textAlign: "center", maxWidth: "600px", margin: "0 auto" }}>
            <div className="section-label">Our Differentiator</div>
            <h2 className="section-title">We Curate, So You <em>Don't Have To</em></h2>
            <p className="section-sub" style={{ margin: "0 auto" }}>Every product on Village Zone is carefully selected for quality, authenticity, and the care behind how it is made.</p>
          </div>
          <div className="curation-grid">
            {curationChecks.map((c, i) => {
              const Icon = c.icon;
              return (
              <div key={i} className="curation-card">
                <div className="curation-icon"><Icon weight="duotone" size={38} /></div>
                <div className="curation-name">{c.name}</div>
                <div className="curation-desc">{c.desc}</div>
              </div>
            )})}
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="testimonials">
          <div className="section-head">
            <div>
              <div className="section-label">From Our Community</div>
              <h2 className="section-title">What customers are <em>saying</em></h2>
            </div>
          </div>
          <div className="test-grid">
            {testimonials.map((t, i) => (
              <div key={i} className="test-card">
                <div className="test-stars">{[...Array(t.stars)].map((_, s) => <span key={s} style={{ color: "var(--amber)" }}>★</span>)}</div>
                <div className="test-quote">"{t.quote}"</div>
                <div className="test-author">
                  <div className="test-avatar">{t.name.split(" ").map((part) => part[0]).join("").slice(0, 2)}</div>
                  <div>
                    <div className="test-name">{t.name}</div>
                    <div className="test-meta">{t.meta}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* VENDOR STORIES */}
        {/* <section class="vendor-stories">
        <div class="stories-header">
          <span class="section-label">Vendor Stories</span>
          <h2 class="section-title">
            Real Stories from Real <em>Vendors</em>
          </h2>
          <p class="section-sub" style={{ margin: "0 auto" }}>
            Discover how passionate entrepreneurs are growing their businesses,
            empowering communities and bringing authentic products to families.
          </p>
        </div>
        <div class="stories-grid">
          <article class="story-card">
            <div class="story-image">
              <img src="images/vendor-story-1.jpg" alt="Vendor Story" />
            </div>
            <div class="story-avatar">
              <img src="images/vendor-avatar-1.jpg" alt="Vendor" />
            </div>
            <div class="story-content">
              <span class="story-category">
                Herbal Products
              </span>
              <h3>
                From Passion to Purpose
              </h3>
              <p>
                Anita transformed her love for traditional healing into a
                thriving business that now reaches thousands of families.
              </p>
              <a href="#">
                Read Story
                <span>→</span>
              </a>
            </div>
          </article>
          <article class="story-card">
            <div class="story-image">
              <img src="images/vendor-story-2.jpg" alt="Vendor Story" />
            </div>
            <div class="story-avatar">
              <img src="images/vendor-avatar-2.jpg" alt="Vendor" />
            </div>
            <div class="story-content">
              <span class="story-category">
                Organic Foods
              </span>
              <h3>
                Building Trust, One Harvest
              </h3>
              <p>
                Ramesh built an organic farming brand that now supplies
                healthy food across the country.
              </p>
              <a href="#">
                Read Story
                <span>→</span>
              </a>
            </div>
          </article>
          <article class="story-card">
            <div class="story-image">
              <img src="images/vendor-story-3.jpg" alt="Vendor Story" />
            </div>
            <div class="story-avatar">
              <img src="images/vendor-avatar-3.jpg" alt="Vendor" />
            </div>
            <div class="story-content">
              <span class="story-category">
                Handmade Goods
              </span>
              <h3>
                Crafting Change Every Day
              </h3>
              <p>
                Meera empowers artisans through handcrafted products while
                preserving generations of traditional craftsmanship.
              </p>
              <a href="#">
                Read Story
                <span>→</span>
              </a>
            </div>
          </article>
        </div>
        <div class="stories-footer">
          <button class="btn-primary">
            View All Stories
          </button>
          <div class="story-dots">
            <span class="active"></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </section> */}

        {/* NEWSLETTER */}
        <section className="newsletter">
          <div className="section-label">Stay Connected</div>
          <h2 className="nl-title">Get seasonal picks <em>straight</em> to your inbox</h2>
          <p className="nl-sub">New local finds, seller spotlights, and seasonal collection updates. Unsubscribe anytime.</p>
          <div className="nl-form">
            <input className="nl-input" type="email" placeholder="your@email.com" />
            <button className="nl-btn">Subscribe →</button>
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePageVillageZone;
